<template>
    <ScrollOverflowIndicators class="navigation-overlay flex">
        <div
            id="navigation-tabs-container"
            class="navigation-tabs-container flex"
            :class="{isReduced: isDisplayModeReduced, isMobile: isDisplayMobile}"
        >
            <template v-if="renderedList.length">
                <template v-for="tab of renderedList" :key="tab.id">
                    <div v-if="tab.ghostChildren?.length" :data-testid="tab.id" class="navigation-tabs-container_ghosts flex">
                        <NavigationTabItem v-for="childTab of tab.ghostChildren" :key="childTab.id" :tab="childTab" />
                    </div>
                    <template v-else>
                        <div v-if="tab.separator === 'top'" :key="`separator-${tab.id}`" class="separator flex"></div>
                        <NavigationTabItem :tab="tab" />
                        <div v-if="tab.separator === 'bottom'" :key="`separator-${tab.id}`" class="separator flex"></div>
                    </template>
                </template>
            </template>
            <NavigationTabsSkeletons v-else />
        </div>
    </ScrollOverflowIndicators>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useUserStore} from '@navbar-logged/features/user';
    import {useNavbarStateStore} from '@navbar-registry';
    import {storeToRefs} from 'pinia';
    import {ScrollOverflowIndicators} from '#components';
    import {useDisplayMobile} from '@malt/utils';
    import type {NavigationTab} from '..';
    import {useAsideMenuStore} from '../aside-menu.store';
    import {useDisplayModeStore} from '../stores';
    import NavigationTabItem from './NavigationTabItem.vue';
    import NavigationTabsSkeletons from './NavigationTabsSkeletons.vue';

    const {isDisplayMobile} = useDisplayMobile();
    const asideMenuStore = useAsideMenuStore();
    const navbarState = useNavbarStateStore();

    const displayModeStore = useDisplayModeStore();
    const {isDisplayModeReduced} = storeToRefs(displayModeStore);

    const userStore = useUserStore();
    const {filteredNavigationRoutes} = storeToRefs(asideMenuStore);

    const renderedList = computed<NavigationTab[]>(() => {
        if (navbarState.pending && !userStore.cachedIdentityRole) {
            return [];
        } else {
            return filteredNavigationRoutes.value;
        }
    });
</script>

<style lang="scss" scoped>
    .navigation-overlay {
        flex-shrink: 1;
        padding-top: var(--joy-core-spacing-2);
        min-height: 0;

        .navigation-tabs-container {
            flex-flow: column nowrap;
            justify-content: flex-start;
            align-items: center;
            height: 100%;
            overflow-y: auto;
            width: 100%;

            &_ghosts {
                flex-flow: column wrap;
                width: 100%;
            }

            .separator {
                height: 1px;
                width: 210px;
                margin: var(--joy-core-spacing-4) auto;
                background-color: var(--joy-color-neutral-30);
                transition:
                    margin var(--joy-transition-duration-default) ease,
                    width var(--joy-transition-duration-default) ease;
                flex: 0 0 auto;
            }

            &.isReduced {
                scrollbar-width: none;
                .separator {
                    width: 22px;
                }
            }
        }
    }
</style>
